import { useDisplayCurrency } from '../currency/DisplayCurrencyProvider';
import { formatAmountLabel, usdOf } from './formatUtils';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Chevron, buttonVariants } from '@charitystack/shared';
import FormStepFrame, { stepHasTitleStrip } from '../Components/FormStepFrame';
import { MONTHLY_BUMP_TITLE_STRIP } from './formDisplayUtils';
import { cn } from '../lib/utils';
import { formatWithCommas } from './formatUtils';
import { trackForm, FORM_EVENTS } from '@charitystack/shared';
import { ANALYTICS_CURRENCY, toMinorUnits } from './analyticsProperties';

/** The upsell's icon disc (Paper E02-0): 56 brand-tint disc, 24 repeat glyph in
 *  the brand action colour, stroke 1.75. */
function RecurringIcon() {
  return (
    <div className="flex h-[var(--cs-size-icon-circle)] w-[var(--cs-size-icon-circle)] shrink-0 items-center justify-center rounded-[var(--cs-radius-full)] bg-[color:var(--cs-brand-tint,var(--cs-surface-1))]">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-[var(--cs-icon-xl)] w-[var(--cs-icon-xl)] text-[color:var(--cs-brand-accent,var(--cs-text-primary))] [stroke-width:var(--cs-icon-stroke)]"
      >
        <path d="m17 2 4 4-4 4" />
        <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
        <path d="m7 22-4-4 4-4" />
        <path d="M21 13v1a4 4 0 0 1-4 4H3" />
      </svg>
    </div>
  );
}

export function MonthlyBumpPage({
  oneTimeAmount,
  monthlyBumpAmount,
  primaryColor,
  brandColors = {},
  onAccept,
  onDecline,
  onBack,
  formDisplayMode = 'dynamic',
  isDrawerOpen = false,
  /** Wider hosted context → use comfortable bump spacing */
  isHostedPage = false,
  elementID,
  /** PostHog: StandardForm / CrowdfundingForm form id */
  formId = null,
  /** PostHog: FORM_TYPES value e.g. standard | crowdfunding */
  formType = null,
  /** Display name from form config (`configData.nonprofitName`) */
  nonprofitName = '',
  bodyStyle = {},
  transitionClassName = '',
  transitionWrapperStyle,
  /** From FormPageShell: true when this step is visible (used so VIEWED fires each visit, not only first mount) */
  isPageActive = false,
  /** When true, disables accept/decline/back buttons to prevent double-submissions */
  isSubmitting = false,
}) {
  const ctx = useDisplayCurrency();
  const safeOneTimeAmount =
    ctx.converted && !Number.isFinite(Number(oneTimeAmount)) ? 0 : oneTimeAmount;
  const safeMonthlyBumpAmount =
    ctx.converted && !Number.isFinite(Number(monthlyBumpAmount)) ? 0 : monthlyBumpAmount;
  const oneTimeUsd = ctx.converted ? usdOf(safeOneTimeAmount, ctx) : oneTimeAmount;
  const monthlyUsd = ctx.converted ? usdOf(safeMonthlyBumpAmount, ctx) : monthlyBumpAmount;
  // Track which button was clicked so only that button shows the spinner
  const [submittedAction, setSubmittedAction] = useState(null); // 'accept' | 'decline' | null

  const organizationLabel = (nonprofitName && String(nonprofitName).trim()) || 'this organization';
  const bumpTitleId = useId().replace(/:/g, '');
  const bumpSectionClass = 'bump flex w-full flex-col items-center gap-[var(--cs-space-2)]';
  // Brand paint arrives through the role tokens on the form's scope (Paper
  // E02-0), so the hex props no longer paint here.
  void primaryColor;
  void brandColors;

  const bumpContextProps = useMemo(
    () => ({
      form_id: formId,
      form_type: formType,
      form_element_id: elementID || null,
      /** User reached upsell after choosing one-time */
      initial_frequency_before_bump: 'ONE_TIME',
      one_time_amount:
        typeof oneTimeUsd === 'number' ? oneTimeUsd : parseFloat(oneTimeUsd) || null,
      one_time_amount_cents: toMinorUnits(oneTimeUsd),
      monthly_bump_amount:
        typeof monthlyUsd === 'number' ? monthlyUsd : parseFloat(monthlyUsd) || null,
      monthly_bump_amount_cents: toMinorUnits(monthlyUsd),
      monthly_bump_accepted: false,
      currency: ANALYTICS_CURRENCY,
      ...(ctx.converted ? { display_currency: ctx.displayCurrency } : {}),
      form_display_mode: formDisplayMode,
      is_drawer_open: isDrawerOpen,
    }),
    [ctx.converted, ctx.displayCurrency, oneTimeUsd, monthlyUsd, formId, formType, elementID, oneTimeAmount, monthlyBumpAmount, formDisplayMode, isDrawerOpen],
  );

  const bumpViewSessionRef = useRef(false);
  useEffect(() => {
    if (isPageActive) {
      if (!bumpViewSessionRef.current) {
        bumpViewSessionRef.current = true;
        try {
          trackForm(FORM_EVENTS.MONTHLY_BUMP_VIEWED, bumpContextProps);
        } catch (e) {
          console.error('[MonthlyBumpPage] analytics (non-blocking):', e);
        }
      }
    } else {
      bumpViewSessionRef.current = false;
    }
  }, [isPageActive, bumpContextProps]);

  const trackAccept = (monthlyAmount) => {
    const safeMonthlyAmount =
      ctx.converted && !Number.isFinite(Number(monthlyAmount)) ? 0 : monthlyAmount;
    const submittedUsd = ctx.converted ? usdOf(safeMonthlyAmount, ctx) : monthlyAmount;
    try {
      trackForm(FORM_EVENTS.MONTHLY_BUMP_CONVERTED_TO_MONTHLY, {
        ...bumpContextProps,
        submitted_frequency: 'MONTHLY',
        submitted_monthly_amount:
          typeof submittedUsd === 'number' ? submittedUsd : parseFloat(submittedUsd) || null,
        submitted_monthly_amount_cents: toMinorUnits(submittedUsd),
        monthly_bump_accepted: true,
        /** Funnel: one-time → monthly upsell accepted */
        one_time_to_monthly_conversion: true,
      });
    } catch (e) {
      console.error('[MonthlyBumpPage] analytics (non-blocking):', e);
    }
    setSubmittedAction('accept');
    onAccept(safeMonthlyAmount);
  };

  const trackDecline = () => {
    try {
      trackForm(FORM_EVENTS.MONTHLY_BUMP_KEPT_ONE_TIME, {
        ...bumpContextProps,
        submitted_frequency: 'ONE_TIME',
        monthly_bump_accepted: false,
        one_time_to_monthly_conversion: false,
      });
    } catch (e) {
      console.error('[MonthlyBumpPage] analytics (non-blocking):', e);
    }
    setSubmittedAction('decline');
    onDecline();
  };

  const trackBack = () => {
    try {
      trackForm(FORM_EVENTS.MONTHLY_BUMP_BACK, bumpContextProps);
    } catch (e) {
      console.error('[MonthlyBumpPage] analytics (non-blocking):', e);
    }
    onBack();
  };

  // The step's Back sits in the title strip, like every step after the first (FormStepFrame);
  // only the drawer, which has no strip of ours, keeps its Back at the bottom.
  const showTitleStrip = stepHasTitleStrip({ isDrawerOpen });

  return (
    <FormStepFrame
      title={MONTHLY_BUMP_TITLE_STRIP}
      onBack={trackBack}
      backDisabled={isSubmitting}
      headerId={elementID ? `${elementID}-CS-monthly-bump-page-header` : undefined}
      isDrawerOpen={isDrawerOpen}
      formDisplayMode={formDisplayMode}
      bodyClassName="monthly-bump-card flex flex-1 w-full flex-col items-center"
      bodyStyle={bodyStyle}
    >
        <div className={transitionClassName} style={transitionWrapperStyle}>
          <div className="flex flex-col flex-1 w-full gap-[var(--cs-space-5)]" style={{ minHeight: 0 }}>
            {/* Icon disc, title and body (Paper E02-0; the "Give monthly" kicker removed, owner call
                2026-10-21: the title strip already names the step). */}
            <div className="mx-auto flex w-full max-w-md flex-col items-center gap-[var(--cs-space-5)] pt-[var(--cs-space-6)] text-center">
              <div aria-hidden="true">
                <RecurringIcon />
              </div>
              <section className={bumpSectionClass} aria-labelledby={bumpTitleId}>
                <h2
                  id={bumpTitleId}
                  className="m-0 font-sans text-[length:var(--cs-text-xl)] leading-[var(--cs-leading-28)] font-semibold text-[color:var(--cs-text-primary)]"
                >
                  Make your gift last
                </h2>
                <p className="m-0 font-sans text-[length:var(--cs-text-sm-plus)] leading-[var(--cs-leading-22)] font-normal text-[color:var(--cs-text-secondary)]">
                  <span className="line">
                    {formatAmountLabel(safeMonthlyBumpAmount, ctx, v => `$${formatWithCommas(v)}`)} a month gives {organizationLabel} the steady support they need to continue making an impact.
                  </span>
                </p>
              </section>
            </div>

            {/* Action buttons — mt-auto pins to bottom in drawer mode */}
            <div className="mt-auto flex w-full flex-col gap-[var(--cs-space-3)] form-action-buttons">
              {/* One-time first, then the monthly CTA (owner call 2026-10-21). DOM order, not a
                  reversed flex, so Tab order and screen readers match what is on screen. */}
              <button
                type="button"
                // The upsell's secondary action: the shared ghost button at the 48 CTA size, no
                // edge, so the monthly CTA leads (owner call 2026-10-21; was the outlined 48).
                className={cn(
                  buttonVariants({ family: 'cta', variant: 'ghost', size: 'cta' }),
                  'dynamic-button-text w-full whitespace-normal',
                )}
                onClick={trackDecline}
                disabled={isSubmitting}
              >
                {isSubmitting && submittedAction === 'decline' ? (
                  <>
                    <span
                      className="inline-block size-[var(--cs-icon)] animate-spin rounded-full border-2 border-current border-t-transparent"
                      role="status"
                      aria-label="Submitting"
                    />
                    <span aria-hidden="true">Processing</span>
                  </>
                ) : (
                  `Give ${formatAmountLabel(safeOneTimeAmount, ctx, v => `$${formatWithCommas(v)}`)} one-time instead`
                )}
              </button>

              {/* The submitting appearance was a ternary here; the cta variant's
                  disabled: classes express the same thing, and `disabled` below
                  is already bound to isSubmitting. */}
              <Button
                type="button"
                family="cta"
                variant="cta"
                size="cta"
                className="dynamic-button-text"
                onClick={() => trackAccept(monthlyBumpAmount)}
                disabled={isSubmitting}
              >
                {isSubmitting && submittedAction === 'accept' ? (
                  <>
                    <span
                      className="inline-block size-[var(--cs-icon)] animate-spin rounded-full border-2 border-current border-t-transparent"
                      role="status"
                      aria-label="Submitting"
                    />
                    <span aria-hidden="true">Processing</span>
                  </>
                ) : (
                  <span className="flex items-center justify-center gap-2 font-semibold">
                    Give {formatAmountLabel(safeMonthlyBumpAmount, ctx, v => `$${formatWithCommas(v)}`)} monthly
                  </span>
                )}
              </Button>

              {!showTitleStrip && (
                <button
                  type="button"
                  className={`form-back-button inline-flex items-center justify-center gap-[var(--cs-space-2)] ${isSubmitting ? 'cursor-not-allowed opacity-[var(--cs-opacity-disabled)]' : ''}`}
                  onClick={trackBack}
                  disabled={isSubmitting}
                >
                  <Chevron direction="left" className="h-[var(--cs-icon)] w-[var(--cs-icon)] shrink-0" />
                  Back
                </button>
              )}
            </div>
          </div>
        </div>
    </FormStepFrame>
  );
}
